import { useState } from "preact/hooks";
import {
  atlasConfig,
  setAtlasConfig,
  applyAtlasPreset,
  matchingPreset,
  ATLAS_PRESETS,
  labEnabled,
} from "../map/atlas-config";

/** Developer control panel for the atlas redesign: every visual decision is a lever
 *  here, presets bundle them into complete looks, and the whole panel only exists in
 *  dev builds or behind ?lab=1 — real visitors never see it. Committing a change of
 *  defaults = editing ATLAS_PRESETS, which "Copy JSON" hands you verbatim. */
export function DesignLab() {
  const [open, setOpen] = useState(true);
  const [copied, setCopied] = useState(false);
  if (!labEnabled()) return null;

  const cfg = atlasConfig.value;
  const preset = matchingPreset();

  function copyJSON() {
    navigator.clipboard
      .writeText(JSON.stringify(atlasConfig.value, null, 2))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      });
  }

  if (!open) {
    return (
      <button class="lab-fab" type="button" onClick={() => setOpen(true)} title="Design Lab">
        ⚙ Lab
      </button>
    );
  }

  return (
    <aside class="design-lab" aria-label="Design Lab">
      <div class="lab-head">
        <strong>Design Lab</strong>
        <span class="lab-preset-tag">{preset ?? "custom"}</span>
        <button class="lab-close" type="button" onClick={() => setOpen(false)} aria-label="Collapse">
          –
        </button>
      </div>

      <div class="lab-row">
        <label>Preset</label>
        <div class="lab-btns">
          {Object.keys(ATLAS_PRESETS).map((name) => (
            <button
              key={name}
              type="button"
              class={preset === name ? "active" : ""}
              onClick={() => applyAtlasPreset(name)}
            >
              {name}
            </button>
          ))}
        </div>
      </div>

      <div class="lab-section">Markers</div>
      <div class="lab-row">
        <label>Mode</label>
        <div class="lab-btns">
          {(["atlas", "dots"] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              class={cfg.markers.mode === mode ? "active" : ""}
              onClick={() => setAtlasConfig({ markers: { mode } })}
            >
              {mode}
            </button>
          ))}
        </div>
      </div>
      <div class="lab-row">
        <label>Label placement</label>
        <div class="lab-btns">
          {(["stable", "dynamic"] as const).map((p) => (
            <button
              key={p}
              type="button"
              class={cfg.markers.placement === p ? "active" : ""}
              onClick={() => setAtlasConfig({ markers: { placement: p } })}
              title={
                p === "stable"
                  ? "Precomputed per-zoom label set — panning never reshuffles labels"
                  : "Raw viewport collision — labels can swap while dragging"
              }
            >
              {p}
            </button>
          ))}
        </div>
      </div>
      <LabRange
        label={`Stable-placement padding ${cfg.markers.stablePadding}px`}
        min={0} max={20} step={1}
        value={cfg.markers.stablePadding}
        onCommit={(v) => setAtlasConfig({ markers: { stablePadding: v } })}
      />
      <div class="lab-row">
        <label>Capital glyph</label>
        <select
          value={cfg.markers.capitalGlyph}
          onChange={(e) =>
            setAtlasConfig({
              markers: {
                capitalGlyph: (e.target as HTMLSelectElement)
                  .value as typeof cfg.markers.capitalGlyph,
              },
            })
          }
        >
          <option value="rings">rings</option>
          <option value="star">star</option>
          <option value="diamond">diamond</option>
          <option value="dot">dot</option>
        </select>
      </div>
      <LabRange
        label={`Capitals: top ${cfg.markers.capitalCount}`}
        min={0} max={120} step={5}
        value={cfg.markers.capitalCount}
        onCommit={(v) => setAtlasConfig({ markers: { capitalCount: v } })}
      />
      <LabRange
        label={`Tier-2 floor: ${cfg.markers.tier2Min} concerts`}
        min={2} max={60} step={2}
        value={cfg.markers.tier2Min}
        onCommit={(v) => setAtlasConfig({ markers: { tier2Min: v } })}
      />
      <LabRange
        label={`Glyph size ×${cfg.markers.sizeScale.toFixed(2)}`}
        min={0.5} max={1.8} step={0.05}
        value={cfg.markers.sizeScale}
        onCommit={(v) => setAtlasConfig({ markers: { sizeScale: v } })}
      />
      <LabRange
        label={`Label size ×${cfg.markers.labelScale.toFixed(2)}`}
        min={0.7} max={1.5} step={0.05}
        value={cfg.markers.labelScale}
        onCommit={(v) => setAtlasConfig({ markers: { labelScale: v } })}
      />
      <LabRange
        label={`Towns appear at z${cfg.markers.townMinzoom.toFixed(1)}`}
        min={0} max={8} step={0.2}
        value={cfg.markers.townMinzoom}
        onCommit={(v) => setAtlasConfig({ markers: { townMinzoom: v } })}
      />
      <LabToggle
        label="Counts as numerals"
        checked={cfg.markers.showCounts}
        onChange={(v) => setAtlasConfig({ markers: { showCounts: v } })}
      />
      <LabToggle
        label="Label required (no bare dots)"
        checked={cfg.markers.labelRequired}
        onChange={(v) => setAtlasConfig({ markers: { labelRequired: v } })}
      />
      <LabToggle
        label="Capitals in spaced caps"
        checked={cfg.markers.capitalCaps}
        onChange={(v) => setAtlasConfig({ markers: { capitalCaps: v } })}
      />

      <div class="lab-section">Suppressed cities</div>
      <div class="lab-row">
        <label>Residual cue</label>
        <div class="lab-btns">
          {(["off", "wash", "stipple", "both"] as const).map((r) => (
            <button
              key={r}
              type="button"
              class={cfg.markers.residual === r ? "active" : ""}
              onClick={() => setAtlasConfig({ markers: { residual: r } })}
              title={
                r === "off"
                  ? "Nothing — an empty region reads the same whether or not it has concerts"
                  : r === "wash"
                    ? "Soft field fed only by cities this zoom suppressed; fades out by itself as labels catch up"
                    : r === "stipple"
                      ? "Grid-capped micro-dots — sharper regional resolution, heavier hand"
                      : "Stipple over wash"
              }
            >
              {r}
            </button>
          ))}
        </div>
      </div>
      <LabRange
        label={`Residual strength ×${cfg.markers.residualStrength.toFixed(2)}`}
        min={0.2} max={2} step={0.1}
        value={cfg.markers.residualStrength}
        onCommit={(v) => setAtlasConfig({ markers: { residualStrength: v } })}
      />
      <LabRange
        label={
          cfg.markers.orphanRadius === 0
            ? "Isolated-town exemption: off"
            : `Isolated-town exemption: ${cfg.markers.orphanRadius}px`
        }
        min={0} max={160} step={10}
        value={cfg.markers.orphanRadius}
        onCommit={(v) => setAtlasConfig({ markers: { orphanRadius: v } })}
      />

      <div class="lab-section">Basemap</div>
      <LabToggle
        label="Rivers"
        checked={cfg.basemap.rivers}
        onChange={(v) => setAtlasConfig({ basemap: { rivers: v } })}
      />
      <LabRange
        label={`River strength ×${cfg.basemap.riverStrength.toFixed(2)}`}
        min={0.2} max={1.6} step={0.1}
        value={cfg.basemap.riverStrength}
        onCommit={(v) => setAtlasConfig({ basemap: { riverStrength: v } })}
      />
      <LabToggle
        label="Urban footprint"
        checked={cfg.basemap.urban}
        onChange={(v) => setAtlasConfig({ basemap: { urban: v } })}
      />
      <LabRange
        label={`Urban strength ×${cfg.basemap.urbanStrength.toFixed(2)}`}
        min={0.2} max={1.6} step={0.1}
        value={cfg.basemap.urbanStrength}
        onCommit={(v) => setAtlasConfig({ basemap: { urbanStrength: v } })}
      />
      <LabToggle
        label="Country names"
        checked={cfg.basemap.countryLabels}
        onChange={(v) => setAtlasConfig({ basemap: { countryLabels: v } })}
      />
      <LabToggle
        label="Ocean & sea names"
        checked={cfg.basemap.marineLabels}
        onChange={(v) => setAtlasConfig({ basemap: { marineLabels: v } })}
      />
      <LabToggle
        label="State/province lines"
        checked={cfg.basemap.states}
        onChange={(v) => setAtlasConfig({ basemap: { states: v } })}
      />
      <div class="lab-row">
        <label>Gazetteer voice</label>
        <div class="lab-btns">
          {(["normal", "dim", "off"] as const).map((voice) => (
            <button
              key={voice}
              type="button"
              class={cfg.basemap.gazetteer === voice ? "active" : ""}
              onClick={() => setAtlasConfig({ basemap: { gazetteer: voice } })}
            >
              {voice}
            </button>
          ))}
        </div>
      </div>

      <div class="lab-foot">
        <button type="button" onClick={copyJSON}>
          {copied ? "Copied ✓" : "Copy config JSON"}
        </button>
      </div>
    </aside>
  );
}

/** Slider that commits on release: every commit rebuilds the whole map style, so
 *  drag-scrubbing must not fire one rebuild per pixel. */
function LabRange(props: {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onCommit: (v: number) => void;
}) {
  return (
    <div class="lab-row">
      <label>{props.label}</label>
      <input
        type="range"
        min={props.min}
        max={props.max}
        step={props.step}
        value={props.value}
        onChange={(e) => props.onCommit(Number((e.target as HTMLInputElement).value))}
      />
    </div>
  );
}

function LabToggle(props: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label class="lab-row lab-toggle">
      <input
        type="checkbox"
        checked={props.checked}
        onChange={(e) => props.onChange((e.target as HTMLInputElement).checked)}
      />
      <span>{props.label}</span>
    </label>
  );
}
